import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { Msp } from '../../../../../types';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';

interface ATSSelectorProps {
  onAtsBussSelect: (value: number) => void;
  onAtsMainBreakerRatingSelect: (value: number) => void;
  onAtsNewExistingChange: (value: NewExistingValue) => void;
  onAtsMainBreakerNewExistingChange: (value: NewExistingValue) => void;
  onAtsPanelNameChange: (value: string) => void;
  onAtsGeneratorNameChange: (value: string) => void;
  selectedAtsBuss?: number;
  selectedAtsMainBreakerRating?: number;
  atsNewExisting?: NewExistingValue;
  atsMainBreakerNewExisting?: NewExistingValue;
  atsPanelName?: string;
  atsGeneratorName?: string;
  atsBussOptions: Msp[];
  atsMainBreakerRatingOptions: Msp[];
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}

export const ATSSelector: React.FC<ATSSelectorProps> = ({
  onAtsBussSelect,
  onAtsMainBreakerRatingSelect,
  onAtsNewExistingChange,
  onAtsMainBreakerNewExistingChange,
  onAtsPanelNameChange,
  onAtsGeneratorNameChange,
  selectedAtsBuss,
  selectedAtsMainBreakerRating,
  atsNewExisting,
  atsMainBreakerNewExisting,
  atsPanelName,
  atsGeneratorName,
  atsBussOptions,
  atsMainBreakerRatingOptions,
  isLoading,
  error,
  isEnabled
}) => {

  const handleAtsBussSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onAtsBussSelect(0);
      return;
    }
    const atsBuss = atsBussOptions?.find(m => m.id === option.id);
    if (atsBuss) {
      onAtsBussSelect(parseInt(atsBuss.value, 10));
    }
  };

  const handleAtsMainBreakerRatingSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onAtsMainBreakerRatingSelect(0);
      return;
    }
    const atsMainBreakerRating = atsMainBreakerRatingOptions?.find(m => m.id === option.id);
    if (atsMainBreakerRating) {
      onAtsMainBreakerRatingSelect(parseInt(atsMainBreakerRating.value, 10));
    }
  };

  const atsBussDropdownOptions = atsBussOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const atsMainBreakerRatingDropdownOptions = atsMainBreakerRatingOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure ATS details</p>
      </div>
    );
  }

  return (
    <div className="bg-transparent p-0">
      <div className="space-y-4">
      {/* Row 1: ATS Buss */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                ATS Buss
              </label>
              {isEnabled && (
                <NewExistingSegment
                  value={atsNewExisting || null}
                  onChange={onAtsNewExistingChange}
                  disabled={!isEnabled}
                />
              )}
            </div>
            <SearchDropdown
              options={atsBussDropdownOptions}
              onSelect={handleAtsBussSelect}
              placeholder="Choose ATS buss rating..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={selectedAtsBuss ? {
                id: atsBussOptions.find(opt => parseInt(opt.value, 10) === selectedAtsBuss)?.id || '',
                label: selectedAtsBuss.toString()
              } : null}
              disabled={!isEnabled}
            />
          </div>
        </div>
      </div>

      {/* Row 2: ATS Main Breaker Rating */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                ATS Main Breaker Rating
              </label>
              {isEnabled && (
                <NewExistingSegment
                  value={atsMainBreakerNewExisting || null}
                  onChange={onAtsMainBreakerNewExistingChange}
                  disabled={!isEnabled}
                />
              )}
            </div>
            <SearchDropdown
              options={atsMainBreakerRatingDropdownOptions}
              onSelect={handleAtsMainBreakerRatingSelect}
              placeholder="Choose ATS main breaker rating..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={selectedAtsMainBreakerRating ? {
                id: atsMainBreakerRatingOptions.find(opt => parseInt(opt.value, 10) === selectedAtsMainBreakerRating)?.id || '',
                label: selectedAtsMainBreakerRating.toString()
              } : null}
              disabled={!isEnabled}
            />
          </div>
        </div>
      </div>

      {/* Row 3: ATS Panel Name + ATS Generator Name */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-6">
          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
            ATS Panel Name
          </label>
          <input
            type="text"
            value={atsPanelName || ''}
            onChange={(e) => onAtsPanelNameChange(e.target.value)}
            className="w-full px-2 py-1.5 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Enter ATS panel name..."
            disabled={!isEnabled}
          />
        </div>

        <div className="col-span-6">
          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
            ATS Generator Name
          </label>
          <input
            type="text"
            value={atsGeneratorName || ''}
            onChange={(e) => onAtsGeneratorNameChange(e.target.value)}
            className="w-full px-2 py-1.5 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Enter ATS generator name..."
            disabled={!isEnabled}
          />
        </div>
      </div>
    </div>
    </div>
  );
};
